import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { text } from '#contract'
import { useClient } from '../app/client-context.ts'
import { useView } from '../app/use-view.ts'
import { useT } from '../i18n/index.ts'
import type { OsMenuItem } from '../os/menu-bar-menu.tsx'
import type { OsMenuHint } from '../os/os.tsx'
import { Os } from '../os/os.tsx'
import styles from './computer-modal.module.css'
import { useOsStates } from './os-states.ts'
import { useDeviceInput } from './use-device-input.ts'

const STAND_UP = text('os.standUp')

/**
 * The computer: the OS's desktop form in a modal inset a little from the view's edges,
 * rounded like a display, over the darkened world. It fades and scales in when the
 * computer opens, and stays (under the pause screen) while paused. Esc, B and "Stand up"
 * ask the game to close it.
 */
export function ComputerModal() {
  const t = useT()
  const client = useClient()
  const { computer } = useOsStates()
  const open = useView((view) => view.os.open === 'computer')
  const playing = useView((view) => view.phase === 'playing')
  const device = useView((view) => view.input.device)
  const root = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(open)
  if (open && !mounted) setMounted(true)
  const active = open && playing

  useDeviceInput(root, active, () => {
    client?.os.close()
  })

  // Cmd or Ctrl+W closes the front window. Browsers keep Ctrl+W for the tab outside
  // fullscreen, so it isn't advertised.
  useEffect(() => {
    if (!active) return
    const controller = new AbortController()
    window.addEventListener(
      'keydown',
      (event) => {
        if (!(event.metaKey || event.ctrlKey) || event.code !== 'KeyW') return
        event.preventDefault()
        const front = computer.front.value
        if (front !== null) computer.close(front)
      },
      { signal: controller.signal },
    )
    return () => {
      controller.abort()
    }
  }, [active, computer])

  const systemItems = useMemo<readonly OsMenuItem[]>(
    () => [{ label: STAND_UP, onSelect: () => client?.os.close() }],
    [client],
  )
  const hint = useMemo<OsMenuHint | null>(() => {
    if (device === 'touch') return null
    return { key: device === 'gamepad' ? 'B' : 'Esc', label: STAND_UP }
  }, [device])
  if (!mounted) return null

  return (
    <div ref={root} class={styles.device} data-open={open || undefined}>
      <div class={styles.scrim} />
      <div
        class={styles.modal}
        role="dialog"
        aria-label={t(text('os.computer'))}
        aria-hidden={!open}
        data-screen={active ? '' : undefined}
      >
        <div class={styles.screen}>
          <Os state={computer} systemItems={systemItems} menuHint={hint} />
        </div>
      </div>
    </div>
  )
}
